import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Route-level error boundary. Renders a friendly fallback + retry button
 * instead of the raw crash screen. Wire this at the App root to catch any
 * unhandled render error from a child component tree.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to console for now; wire to a real telemetry service later.
    console.error("ErrorBoundary caught", error, info);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  hardReload = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="flex min-h-screen flex-col items-center justify-center bg-neutral-50 p-6"
          data-testid="error-boundary-fallback"
        >
          <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-8 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-50">
              <AlertTriangle className="h-7 w-7 text-amber-600" />
            </div>
            <h1 className="text-xl font-bold text-neutral-900">
              Something broke on our side.
            </h1>
            <p className="mt-2 text-sm text-neutral-500">
              The page ran into an unexpected error. Your data is safe.
            </p>
            {this.state.error?.message && (
              <p className="mt-4 rounded-lg bg-neutral-50 p-3 text-left font-mono text-[11px] text-neutral-500">
                {String(this.state.error.message).slice(0, 200)}
              </p>
            )}
            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
              <Button
                onClick={this.reset}
                variant="outline"
                data-testid="error-retry-btn"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Try again
              </Button>
              <Button
                onClick={this.hardReload}
                className="bg-amber-700 hover:bg-amber-800"
                data-testid="error-home-btn"
              >
                Go to home
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
